/**
 * Classify attachments by extension/MIME for UI rendering.
 */

export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const ATTACHMENT_MAX_COUNT = 20;

export const ALLOWED_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "pdf",
  "txt",
  "doc",
  "docx",
  "ovpn",
  "wg",
  "zip",
];

const IMAGE_EXT = new Set(["jpg", "jpeg", "png", "gif", "webp"]);
const DOC_EXT = new Set(["doc", "docx"]);

export class AttachmentDisplayClassifier {
  /**
   * @param {{ file_name?: string, mime_type?: string }} att
   * @returns {'image' | 'pdf' | 'document' | 'text' | 'other'}
   */
  classify(att) {
    const ext = this.extensionFromName(att?.file_name);
    const mime = (att?.mime_type || "").toLowerCase();
    if (IMAGE_EXT.has(ext) || mime.startsWith("image/")) return "image";
    if (ext === "pdf" || mime === "application/pdf") return "pdf";
    if (DOC_EXT.has(ext) || mime.includes("wordprocessingml") || mime === "application/msword") {
      return "document";
    }
    if (ext === "txt" || ext === "ovpn" || ext === "wg" || mime.startsWith("text/")) {
      return "text";
    }
    return "other";
  }

  /**
   * @param {string} [fileName]
   */
  extensionFromName(fileName) {
    const name = (fileName || "").trim().toLowerCase();
    const dot = name.lastIndexOf(".");
    if (dot < 0) return "";
    return name.slice(dot + 1);
  }

  /**
   * @param {File} file
   * @returns {{ ok: true } | { ok: false, error: string }}
   */
  validateFile(file) {
    if (!file) return { ok: false, error: "No file selected." };
    if (file.size > ATTACHMENT_MAX_BYTES) {
      return { ok: false, error: "File exceeds 10 MB limit." };
    }
    const ext = this.extensionFromName(file.name);
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return { ok: false, error: `File type .${ext || "?"} is not allowed.` };
    }
    return { ok: true };
  }

  /**
   * @param {number} bytes
   */
  formatFileSize(bytes) {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }

  acceptAttribute() {
    return ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(",");
  }
}

export const attachmentDisplayClassifier = new AttachmentDisplayClassifier();
